import { Injectable } from '@angular/core';
import type { BalanceReportFormatFindInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-format-find.dto';
import type { BalanceReportFormatFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-format-form.dto';
import type { BalanceReportFormatFindManyQueryInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-format-find-many-query.dto';
import type { ClassificationTypeFindInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-type-find.dto';
import type { ClassificationTypeFindManyQueryInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-type-find-many-query.dto';
import type { BalanceReportSortingFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-sorting/balance-report-sorting-form.dto';
import type { BalanceReportComputedRowFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-computed-row-form.dto';
import type { BalanceReportComputedRowFindInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-computed-row-find.dto';
import type { BalanceReportRowTokenInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-row-token.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BalanceReportFormatService extends BaseService {
  protected baseUrl = '/finance/plan-fact/balance-report-format';

  private readonly classificationTypesChanged = new Subject<void>();

  public readonly classificationTypesChanged$ = this.classificationTypesChanged.asObservable();

  public notifyClassificationTypesChanged(): void {
    this.classificationTypesChanged.next();
  }

  public findMany(query?: BalanceReportFormatFindManyQueryInterface): Observable<BalanceReportFormatFindInterface[]> {
    return this.http.get<BalanceReportFormatFindInterface[]>(this.baseUrl, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  public findOne(id: number): Observable<BalanceReportFormatFindInterface> {
    return this.http.get<BalanceReportFormatFindInterface>(`${this.baseUrl}/${id}`);
  }

  public findOneForm(id: number): Observable<BalanceReportFormatFormInterface> {
    return this.http.get<BalanceReportFormatFormInterface>(`${this.baseUrl}/${id}/form`);
  }

  public createOne(body: BalanceReportFormatFormInterface): Observable<BalanceReportFormatFindInterface> {
    return this.http.post<BalanceReportFormatFindInterface>(this.baseUrl, body);
  }

  public updateOne(id: number, body: BalanceReportFormatFormInterface): Observable<BalanceReportFormatFindInterface> {
    return this.http.put<BalanceReportFormatFindInterface>(`${this.baseUrl}/${id}`, body);
  }

  public deleteOne(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  public findClassificationTypes(
    query?: ClassificationTypeFindManyQueryInterface
  ): Observable<ClassificationTypeFindInterface[]> {
    return this.http.get<ClassificationTypeFindInterface[]>(`${this.baseUrl}/classification-type`, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  public setSorting(id: number, body: BalanceReportSortingFormInterface): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/sorting`, body);
  }

  public getTokens(formatId: number): Observable<BalanceReportRowTokenInterface[]> {
    return this.http.get<BalanceReportRowTokenInterface[]>(`${this.baseUrl}/${formatId}/tokens`);
  }

  public findOneComputedRow(formatId: number, rowId: number): Observable<BalanceReportComputedRowFindInterface> {
    return this.http.get<BalanceReportComputedRowFindInterface>(
      `${this.baseUrl}/${formatId}/computed-row/${rowId}`
    );
  }

  public createComputedRow(
    formatId: number,
    body: BalanceReportComputedRowFormInterface
  ): Observable<BalanceReportComputedRowFindInterface> {
    return this.http.post<BalanceReportComputedRowFindInterface>(`${this.baseUrl}/${formatId}/computed-row`, body);
  }

  public updateComputedRow(
    formatId: number,
    rowId: number,
    body: BalanceReportComputedRowFormInterface
  ): Observable<BalanceReportComputedRowFindInterface> {
    return this.http.put<BalanceReportComputedRowFindInterface>(
      `${this.baseUrl}/${formatId}/computed-row/${rowId}`,
      body
    );
  }

  public deleteComputedRow(formatId: number, rowId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${formatId}/computed-row/${rowId}`);
  }
}
